/**
 * Namespaced localStorage wrapper.
 *
 * Direct localStorage access from anywhere else in the codebase is forbidden
 * by ESLint (`no-restricted-syntax` in eslint.config.js) — use these helpers.
 *
 * Tokens are FORBIDDEN here. The runtime guard throws StorageGuardError on
 * keys matching credential-like patterns. If you find yourself wanting to
 * store a token, stop and read:
 *   - docs/adr/0009-storage-architecture-and-token-strategy.md
 *   - docs/threat-model.md
 *
 * Namespace policy (see ADR-0009 for the full table):
 *   - prefs : device-scoped, survives logout (locale, theme)
 *   - ui    : device-scoped, survives logout (sidebar, column widths, warehouse_id)
 *   - draft : user-scoped, cleared on logout (form drafts)
 */

const NS = {
  prefs: 'glasspro:prefs:',
  ui: 'glasspro:ui:',
  draft: 'glasspro:draft:',
} as const

const FORBIDDEN_KEY_PATTERN =
  /token|password|secret|credential|api[_-]?key|jwt|bearer|auth/i

export class StorageGuardError extends Error {
  constructor(key: string) {
    super(
      `[storage] Refusing to store key "${key}" — appears credential-like. ` +
        'Tokens belong in src/shared/storage/memory.ts. ' +
        'See docs/adr/0009-storage-architecture-and-token-strategy.md',
    )
    this.name = 'StorageGuardError'
  }
}

function safeSet(key: string, value: string): void {
  if (FORBIDDEN_KEY_PATTERN.test(key)) {
    throw new StorageGuardError(key)
  }
  try {
    localStorage.setItem(key, value)
  } catch (e) {
    // Quota exceeded, private browsing, disabled storage — degrade gracefully.
    if (import.meta.env.DEV) throw e
    console.warn('[storage] setItem failed:', key, e)
  }
}

function listKeys(prefix: string): string[] {
  return Object.keys(localStorage).filter((k) => k.startsWith(prefix))
}

function clearNamespace(prefix: string): void {
  listKeys(prefix).forEach((k) => localStorage.removeItem(k))
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (raw === null) return null
  try {
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

export const localStore = {
  prefs: {
    getLocale: (): string | null => localStorage.getItem(`${NS.prefs}locale`),
    setLocale: (l: string) => safeSet(`${NS.prefs}locale`, l),
    getTheme: (): string | null => localStorage.getItem(`${NS.prefs}theme`),
    setTheme: (t: string) => safeSet(`${NS.prefs}theme`, t),
  },

  ui: {
    setSidebarCollapsed: (collapsed: boolean) =>
      safeSet(`${NS.ui}sidebar`, String(collapsed)),
    getSidebarCollapsed: (): boolean =>
      localStorage.getItem(`${NS.ui}sidebar`) === 'true',

    setColumnWidths: (tableId: string, widths: number[]) =>
      safeSet(`${NS.ui}cols:${tableId}`, JSON.stringify(widths)),
    getColumnWidths: (tableId: string): number[] | null =>
      safeJsonParse<number[]>(localStorage.getItem(`${NS.ui}cols:${tableId}`)),

    setWarehouseId: (id: string) => safeSet(`${NS.ui}warehouse_id`, id),
    getWarehouseId: (): string | null =>
      localStorage.getItem(`${NS.ui}warehouse_id`),
  },

  draft: {
    set: (id: string, value: unknown) =>
      safeSet(`${NS.draft}${id}`, JSON.stringify(value)),
    get: <T>(id: string): T | null =>
      safeJsonParse<T>(localStorage.getItem(`${NS.draft}${id}`)),
    remove: (id: string) => localStorage.removeItem(`${NS.draft}${id}`),
    list: (): string[] =>
      listKeys(NS.draft).map((k) => k.slice(NS.draft.length)),
    clear: () => clearNamespace(NS.draft),
  },

  /**
   * Called on logout, 401-after-refresh-failure, and account switch.
   *
   * Preserves prefs and ui namespaces (device-scoped — locale, theme,
   * sidebar). Clears draft namespace (user-scoped — form drafts). Does not
   * touch the in-memory token (caller is responsible for tokenMemory.clear()).
   */
  clearUserScoped: () => clearNamespace(NS.draft),
} as const
